import * as React from 'react';

import {
    getIconAssetURL,
    getIconStyle,
    type IconName,
    IconStyle,
    type IconsContextType,
    type InlineIconSource,
} from '@gitbook/icons';
import { getInlineIconSourceKey } from '@gitbook/icons/IconSources';

const LoadIconSourcesContext = React.createContext<LoadIconSources>(() => {});
const pendingIconSourceLoads = new Map<string, Promise<Record<string, InlineIconSource>>>();

export type IconSourceRequest = IconName | { icon: IconName; iconStyle?: IconStyle };

type LoadIconSources = (icons: IconSourceRequest | IconSourceRequest[]) => void;

type IconSourceAssets = Pick<IconsContextType, 'assetsByStyles' | 'assetsURL' | 'assetsURLToken'>;

/**
 * Hook that provides the current inline SVG source map and a loader for requested icons.
 */
export function useInlineIconSources(assets: IconSourceAssets) {
    const [iconSources, setIconSources] = React.useState<Record<string, InlineIconSource>>({});
    const iconSourcesRef = React.useRef(iconSources);

    React.useEffect(() => {
        iconSourcesRef.current = iconSources;
    }, [iconSources]);

    const loadIconSources = React.useCallback<LoadIconSources>(
        (icons) => {
            const iconsToLoad = Array.isArray(icons) ? icons : [icons];
            const missingIcons = iconsToLoad.filter((request) => {
                const [style, resolvedIcon] = getIconStyle(
                    getIconSourceRequestStyle(request),
                    getIconSourceRequestName(request)
                );
                return !iconSourcesRef.current[getInlineIconSourceKey(style, resolvedIcon)];
            });

            if (missingIcons.length === 0) {
                return;
            }

            fetchIconSources(assets, missingIcons)
                .then((sources) => {
                    setIconSources((prev) => ({ ...prev, ...sources }));
                })
                .catch(() => {
                    // Inline sources are loaded opportunistically; failed requests can be retried later.
                });
        },
        [assets]
    );

    return { iconSources, loadIconSources };
}

/**
 * Provides the inline SVG source loader to content icon renderers.
 */
export function IconSourcesProvider(props: { children: React.ReactNode; load: LoadIconSources }) {
    return (
        <LoadIconSourcesContext.Provider value={props.load}>
            {props.children}
        </LoadIconSourcesContext.Provider>
    );
}

/**
 * Hook used by content icons to load inline SVG source markup.
 */
export function useLoadIconSources() {
    return React.useContext(LoadIconSourcesContext);
}

function fetchIconSources(
    assets: IconSourceAssets,
    icons: IconSourceRequest[]
): Promise<Record<string, InlineIconSource>> {
    return Promise.all(
        icons.map((request) => {
            const [style, resolvedIcon] = getIconStyle(
                getIconSourceRequestStyle(request),
                getIconSourceRequestName(request)
            );
            const key = getInlineIconSourceKey(style, resolvedIcon);
            const cachedPromise =
                pendingIconSourceLoads.get(key) ??
                fetch(
                    getIconAssetURL(
                        { ...assets, iconStyle: IconStyle.Regular },
                        style,
                        String(resolvedIcon)
                    )
                )
                    .then(async (response) => {
                        if (!response.ok) {
                            throw new Error(
                                `Unable to load inline source for ${key}: ${response.status} ${response.statusText}`
                            );
                        }

                        return {
                            [key]: parseSVGSource(await response.text()),
                        };
                    })
                    .finally(() => {
                        pendingIconSourceLoads.delete(key);
                    });

            pendingIconSourceLoads.set(key, cachedPromise);
            return cachedPromise;
        })
    ).then((sources) => Object.assign({}, ...sources));
}

function getIconSourceRequestName(request: IconSourceRequest): IconName {
    return typeof request === 'string' ? request : request.icon;
}

function getIconSourceRequestStyle(request: IconSourceRequest): IconStyle {
    return typeof request === 'string'
        ? IconStyle.Regular
        : (request.iconStyle ?? IconStyle.Regular);
}

function parseSVGSource(svg: string): InlineIconSource {
    const svgMatch = svg.match(/<svg\b([^>]*)>([\s\S]*)<\/svg>\s*$/i);
    if (!svgMatch) {
        throw new Error('Unable to parse SVG source');
    }

    const viewBoxMatch = svgMatch[1].match(/\bviewBox=(["'])(.*?)\1/i);
    if (!viewBoxMatch) {
        throw new Error('SVG source is missing a viewBox');
    }

    return {
        viewBox: viewBoxMatch[2],
        markup: svgMatch[2].replace(/<!--[\s\S]*?-->/g, '').trim(),
    };
}
